// Máy khách HTTP tới gateway. Access token chỉ nằm trong bộ nhớ; refresh token là cookie httpOnly.
// 401 do token hết hạn ⇒ làm mới một lần (dùng chung cho các request đồng thời) rồi gửi lại.
import type { AppErrorBody, ErrorCode } from '@cathouse/contracts';
import { API_BASE } from '../config';
import { syncServerDate, syncServerTime } from '../lib/server-clock';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly reason?: string;
  readonly details?: Record<string, unknown>;
  readonly status: number;
  constructor(status: number, body: Partial<AppErrorBody>) {
    super(body.message || 'Lỗi kết nối máy chủ');
    this.status = status;
    this.code = (body.code as ErrorCode) || 'INTERNAL';
    this.reason = body.reason;
    this.details = body.details;
  }
}

type Listener<T> = (v: T) => void;
function emitter<T>() {
  const ls = new Set<Listener<T>>();
  return {
    on: (l: Listener<T>) => {
      ls.add(l);
      return () => void ls.delete(l);
    },
    emit: (v: T) => ls.forEach((l) => l(v)),
  };
}

let accessToken: string | null = null;
/** Phiên kết thúc (refresh thất bại / bị thu hồi) ⇒ giao diện quay về màn đăng nhập. */
export const sessionEnded = emitter<{ reason?: string }>();
/** Gateway báo quyền đã đổi (X-Authz-Version) ⇒ nạp lại bootstrap + làm mới token. */
export const authzChanged = emitter<number>();
/** Tài khoản vừa bị khoá / ngừng hoạt động / module bị tắt ⇒ nạp lại bootstrap để về đúng cổng vào. */
export const accessChanged = emitter<string>();
const ACCESS_REASONS = new Set(['ACCOUNT_LOCKED', 'ACCOUNT_INACTIVE', 'MODULE_DISABLED']);

export const getAccessToken = () => accessToken;
export const setAccessToken = (t: string | null) => {
  accessToken = t;
};

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
  sessionId: string;
}

let refreshing: Promise<boolean> | null = null;
/**
 * Làm mới access token bằng cookie. Trả false nếu không còn phiên: 204 (gateway — không có cookie refresh, khách chưa đăng nhập;
 * không phải lỗi nên trình duyệt không ghi 401) hoặc lỗi (cookie sai / hết hạn / bị thu hồi).
 */
export function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'same-origin' })
    .then(async (r) => {
      if (!r.ok || r.status === 204) {
        accessToken = null;
        return false;
      }
      accessToken = ((await r.json()) as TokenResponse).accessToken;
      return true;
    })
    .catch(() => false)
    .finally(() => (refreshing = null));
  return refreshing;
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | null | undefined | string[]>;
  body?: unknown;
  headers?: Record<string, string>;
  /** Khoá lạc quan: gửi If-Match với phiên bản đang sửa. */
  ifMatch?: number;
  signal?: AbortSignal;
}

const RETRY_REASONS = new Set(['TOKEN_EXPIRED', 'TOKEN_INVALID', 'TOKEN_MISSING']);

function url(path: string, query?: RequestOptions['query']) {
  const u = new URL(API_BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    u.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  return u.pathname + u.search;
}

/** Header chung của mọi request (token, If-Match, Idempotency-Key cho POST). */
function headersFor(method: string, o: RequestOptions, accept: string) {
  const headers: Record<string, string> = { Accept: accept, ...(o.headers ?? {}) };
  if (o.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (o.ifMatch !== undefined) headers['If-Match'] = String(o.ifMatch);
  // Gửi lại do mạng chập chờn không tạo bản ghi trùng
  if (method === 'POST' && !headers['Idempotency-Key']) headers['Idempotency-Key'] = crypto.randomUUID();
  return headers;
}

/**
 * Phản hồi có thể đến từ bộ nhớ đệm HTTP (Age, Cache-Control max-age/immutable) ⇒ header giờ là giờ lúc máy chủ tạo
 * bản đệm (có thể cũ cả ngày), không dùng để hiệu chỉnh đồng hồ.
 */
export function cacheableResponse(h: Headers) {
  if (h.has('Age')) return true;
  const cc = (h.get('Cache-Control') ?? '').toLowerCase();
  return /\bimmutable\b/.test(cc) || /\b(max-age|s-maxage)\s*=\s*0*[1-9]/.test(cc);
}

/** Hiệu chỉnh đồng hồ (chỉ phản hồi không đệm được): X-Server-Time (chính xác), dự phòng header Date. */
function syncClock(r: Response) {
  if (cacheableResponse(r.headers)) return;
  const serverTime = r.headers.get('X-Server-Time');
  if (serverTime) syncServerTime(serverTime);
  else syncServerDate(r.headers.get('Date'));
}

/** Gửi request; đọc header chung của phản hồi (phiên bản quyền, giờ máy chủ). Mất mạng ⇒ ApiError UNAVAILABLE. */
async function exchange(method: string, path: string, o: RequestOptions, accept: string, clock = true): Promise<Response> {
  let r: Response;
  try {
    r = await fetch(url(path, o.query), { method, headers: headersFor(method, o, accept), body: o.body !== undefined ? JSON.stringify(o.body) : undefined, credentials: 'same-origin', signal: o.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, { code: 'UNAVAILABLE', message: 'Không kết nối được máy chủ. Kiểm tra mạng và thử lại.' });
  }
  const pv = r.headers.get('X-Authz-Version');
  if (pv) authzChanged.emit(Number(pv));
  if (clock) syncClock(r);
  return r;
}

const parseError = (text: string): unknown => {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
};

/** Phản hồi lỗi ⇒ ném ApiError; 401 do token hết hạn (chưa thử lại) ⇒ làm mới phiên rồi trả về để gửi lại một lần. */
async function failOrRetry(status: number, data: unknown, retried: boolean): Promise<void> {
  const err = new ApiError(status, ((data as { error?: AppErrorBody } | null)?.error ?? {}) as AppErrorBody);
  if (status === 401 && !retried && RETRY_REASONS.has(err.reason ?? '') && (await refreshSession())) return;
  if (status === 401) sessionEnded.emit({ reason: err.reason });
  if (err.reason && ACCESS_REASONS.has(err.reason)) accessChanged.emit(err.reason);
  throw err;
}

async function send<T>(method: string, path: string, o: RequestOptions = {}, retried = false): Promise<T> {
  const r = await exchange(method, path, o, 'application/json');
  const text = await r.text();
  if (r.ok) return (text ? JSON.parse(text) : null) as T;
  await failOrRetry(r.status, parseError(text), retried);
  return send<T>(method, path, o, true);
}

export const http = {
  get: <T>(path: string, o?: RequestOptions) => send<T>('GET', path, o),
  post: <T>(path: string, body?: unknown, o?: RequestOptions) => send<T>('POST', path, { ...o, body: body ?? {} }),
  put: <T>(path: string, body?: unknown, o?: RequestOptions) => send<T>('PUT', path, { ...o, body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, o?: RequestOptions) => send<T>('PATCH', path, { ...o, body: body ?? {} }),
  del: <T>(path: string, o?: RequestOptions) => send<T>('DELETE', path, o),
};

/** Tên tệp trong Content-Disposition (ưu tiên filename*=UTF-8''…). */
export function fileNameFrom(disposition: string | null | undefined): string | null {
  if (!disposition) return null;
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(disposition);
  if (star) {
    try {
      return decodeURIComponent(star[2]!.trim().replace(/^"|"$/g, ''));
    } catch {
      /* rơi xuống filename= */
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(disposition);
  return plain ? (plain[1] ?? plain[2] ?? '').trim() || null : null;
}

async function blobOnce(method: 'GET' | 'POST', path: string, o: RequestOptions, retried: boolean): Promise<{ blob: Blob; fileName: string | null }> {
  // Tệp có thể được đệm (Cache-Control dài) ⇒ không hiệu chỉnh đồng hồ theo phản hồi tệp
  const r = await exchange(method, path, o, '*/*', false);
  if (r.ok) return { blob: await r.blob(), fileName: fileNameFrom(r.headers.get('Content-Disposition')) };
  await failOrRetry(r.status, parseError(await r.text()), retried);
  return blobOnce(method, path, o, true);
}

/** Tải tệp (xuất CSV, tệp xuất khách…) với cùng cơ chế token / 401 / lỗi như http. */
export function httpBlob(method: 'GET' | 'POST', path: string, body?: unknown, o: RequestOptions = {}): Promise<{ blob: Blob; fileName: string | null }> {
  return blobOnce(method, path, { ...o, body: method === 'POST' ? (body ?? {}) : undefined }, false);
}

/** Lưu Blob về máy người dùng với tên tệp cho trước. */
export function saveBlob(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 30_000);
}

/** Đăng nhập / đăng ký / tạo công ty: lưu access token trả về. */
export async function signIn(path: '/auth/login' | '/auth/register' | '/tenants', body: unknown) {
  const r = await http.post<TokenResponse & Record<string, unknown>>(path, body);
  accessToken = r.accessToken;
  return r;
}
export async function signOut(all = false) {
  try {
    await http.post('/auth/logout', { all });
  } finally {
    accessToken = null;
  }
}

/** Thông điệp lỗi để hiển thị (toast/biểu mẫu). */
export const errorMessage = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Đã có lỗi xảy ra');
